import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, Input } from '@angular/core';
import { MessageService, SortEvent } from 'primeng/api';
import { Router } from '@angular/router';
import { MarcaDTO, MarcaFormDTO } from '../../models/marca.model';
import { MarcaService } from '../../services/marca/marca.service';
import { TranslateService } from '@ngx-translate/core';
import { TableColumn } from '../../models/shared/table-column.model';
import { Subscription } from 'rxjs';
import { FileUpload } from 'primeng/fileupload';
import { environment } from '../../environments/environment';
import { ImagemUtils } from '../../shared/utils/imagem-utils';

@Component({
  selector: 'app-marca',
  templateUrl: './marca.component.html',
  styleUrl: './marca.component.scss',
  providers: [MessageService],
})
export class MarcaComponent implements OnInit, OnDestroy {
  /**
   * Modo EMBUTIDO (Operar Cliente / Área do Contador): opera sob o CONTEXTO do
   * cliente (X-Empresa-Contexto). Esconde o page-header e oculta o excluir
   * (backend não libera DELETE /marca no contexto).
   */
  @Input() embutido = false;

  /** Id da empresa do CLIENTE (só no modo embutido). Usado nas URLs de imagem. */
  @Input() empresaContextoId?: number | null;

  env = environment;
  idEmpresa: string = '';

  marcas: MarcaDTO[] = [];
  marcasOriginal: MarcaDTO[] = [];
  first: number = 0;
  rows: number = 10;
  private _lastRows: number = 10;
  totalRecords: number = 0;
  loading: boolean = true;
  searchNome: string = '';
  lastfield: string = 'id';
  lastSort: string = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  form: MarcaFormDTO = this.createEmptyForm();
  isFormSubmitted = false;
  submitting = false;
  activeTabIndex: number = 1;

  // Upload / preview
  imageBase64: string | null = null;
  imagePreviewUrl: string | null = null;
  fileName: string | null = null;

  // ViewChild para resetar o p-fileUpload após salvamento
  @ViewChild('fileUpload') fileUploadRef!: FileUpload;

  // ViewChild para foco no campo de busca (F2)
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;

  // ViewChild para foco no botao de confirmar exclusao
  @ViewChild('deleteConfirmBtn') deleteConfirmBtnRef!: ElementRef<HTMLButtonElement>;

  // Dialog de exclusao
  deleteDialogVisible = false;
  deleteMarcaId: string | null = null;

  // Configuracao de colunas visiveis
  readonly STORAGE_KEY = 'marca-visible-columns';

  columns: TableColumn[] = [
    { key: 'imagem', labelKey: 'marca.labels.imagem', visible: true, fixed: true, defaultVisible: true },
    { key: 'codigoMarca', labelKey: 'marca.labels.codigo_marca', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'marca.labels.nome', visible: true, fixed: true, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'marca.labels.data_da_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'marca.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  // Objetos de traducao (padrao moderno - tipo literal implicito)
  texts = {
    titulo: '',
    subtitulo: '',
    add_nova_marca: '',
    lista_de_marcas: '',
    confirmar_exclusao: '',
    confirmacao: '',
    nenhum_marca: '',
    ajuste_filtros: '',
    carregando: '',
    erro: '',
    sucesso: '',
    nome_obrigatorio: '',
    arquivo_invalido: '',
    marca_adicionada_sucesso: '',
    marca_editada_sucesso: '',
    erro_ao_salvar_marca: '',
    marca_excluido_sucesso: '',
    erro_ao_excluir_marca: '',
    pre_visualizacao: '',
    aba_cadastro: '',
    aba_lista: '',
  };

  labels = {
    nome: '',
    imagem: '',
    codigo_marca: '',
    data_da_inclusao: '',
    acoes: '',
    itens_por_pagina: '',
  };

  placeholders = {
    nome_da_marca: '',
    pesquisar: '',
  };

  botoes = {
    adicionar: '',
    selecionar_imagem: '',
    remover: '',
    salvar: '',
    limpar: '',
    nova_marca: '',
    confirmar: '',
    cancelar: '',
  };

  tooltips = {
    editar: '',
    excluir: '',
    ver_imagem: '',
  };

  // Dialog de imagem expandida
  displayDialogImagem = false;
  imagemExpandidaUrl: string = '';

  private langChangeSub!: Subscription;

  constructor(
    private messageService: MessageService,
    private marcaService: MarcaService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.idEmpresa = this.embutido
      ? String(this.empresaContextoId ?? '')
      : (sessionStorage.getItem('id') || '');
    this.loadColumnPreferences();
    this.loadTranslations();
    this.findMarca();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  // =========================================================================
  // Traducoes
  // =========================================================================

  onTabChange(event: { index: number }): void {
    if (event.index === 1) {
      this.texts.titulo = this.translate.instant('marca.texts.titulo_criar');
      this.texts.subtitulo = this.translate.instant('marca.texts.subtitulo_lista');
    } else {
      const isEdicao = !!this.form.id;
      this.texts.titulo = this.translate.instant(
        isEdicao ? 'marca.texts.titulo_editar' : 'marca.texts.titulo_criar'
      );
      this.texts.subtitulo = this.translate.instant(
        isEdicao ? 'marca.texts.subtitulo_editar' : 'marca.texts.subtitulo_criar'
      );
    }
  }

  private loadTranslations(): void {
    const isEdicao = !!this.form.id;
    const isListaAtiva = this.activeTabIndex === 1;
    this.texts = {
      titulo: this.translate.instant(
        isListaAtiva ? 'marca.texts.titulo_criar' : isEdicao ? 'marca.texts.titulo_editar' : 'marca.texts.titulo_criar'
      ),
      subtitulo: this.translate.instant(
        isListaAtiva
          ? 'marca.texts.subtitulo_lista'
          : isEdicao ? 'marca.texts.subtitulo_editar' : 'marca.texts.subtitulo_criar'
      ),
      add_nova_marca: this.translate.instant('marca.texts.add_nova_marca'),
      lista_de_marcas: this.translate.instant('marca.texts.lista_de_marcas'),
      confirmar_exclusao: this.translate.instant('marca.texts.confirmar_exclusao'),
      confirmacao: this.translate.instant('marca.texts.confirmacao'),
      nenhum_marca: this.translate.instant('marca.texts.nenhum_marca'),
      ajuste_filtros: this.translate.instant('marca.texts.ajuste_filtros'),
      carregando: this.translate.instant('marca.texts.carregando'),
      erro: this.translate.instant('marca.texts.erro'),
      sucesso: this.translate.instant('marca.texts.sucesso'),
      nome_obrigatorio: this.translate.instant('marca.texts.nome_obrigatorio'),
      arquivo_invalido: this.translate.instant('marca.texts.arquivo_invalido'),
      marca_adicionada_sucesso: this.translate.instant('marca.texts.marca_adicionada_sucesso'),
      marca_editada_sucesso: this.translate.instant('marca.texts.marca_editada_sucesso'),
      erro_ao_salvar_marca: this.translate.instant('marca.texts.erro_ao_salvar_marca'),
      marca_excluido_sucesso: this.translate.instant('marca.texts.marca_excluido_sucesso'),
      erro_ao_excluir_marca: this.translate.instant('marca.texts.erro_ao_excluir_marca'),
      pre_visualizacao: this.translate.instant('marca.texts.pre_visualizacao'),
      aba_cadastro: this.translate.instant('marca.texts.aba_cadastro'),
      aba_lista: this.translate.instant('marca.texts.aba_lista'),
    };

    this.labels = {
      nome: this.translate.instant('marca.labels.nome'),
      imagem: this.translate.instant('marca.labels.imagem'),
      codigo_marca: this.translate.instant('marca.labels.codigo_marca'),
      data_da_inclusao: this.translate.instant('marca.labels.data_da_inclusao'),
      acoes: this.translate.instant('marca.labels.acoes'),
      itens_por_pagina: this.translate.instant('marca.labels.itens_por_pagina'),
    };

    this.placeholders = {
      nome_da_marca: this.translate.instant('marca.placeholders.nome_da_marca'),
      pesquisar: this.translate.instant('marca.placeholders.pesquisar'),
    };

    this.botoes = {
      adicionar: this.translate.instant('marca.botoes.adicionar'),
      selecionar_imagem: this.translate.instant('marca.botoes.selecionar_imagem'),
      remover: this.translate.instant('marca.botoes.remover'),
      salvar: this.translate.instant('marca.botoes.salvar'),
      limpar: this.translate.instant('marca.botoes.limpar'),
      nova_marca: this.translate.instant('marca.botoes.nova_marca'),
      confirmar: this.translate.instant('marca.botoes.confirmar'),
      cancelar: this.translate.instant('marca.botoes.cancelar'),
    };

    this.tooltips = {
      editar: this.translate.instant('marca.tooltips.editar'),
      excluir: this.translate.instant('marca.tooltips.excluir'),
      ver_imagem: this.translate.instant('marca.tooltips.ver_imagem'),
    };
  }

  // =========================================================================
  // FILE UPLOAD
  // =========================================================================

  async onSelectImage(event: any) {
    const file: File | undefined = event.files?.[0];
    if (!file) { return; }

    if (!file.type.startsWith('image/')) {
      this.messageService.add({
        severity: 'error',
        summary: this.texts.erro,
        detail: this.texts.arquivo_invalido,
      });
      return;
    }

    this.fileName = file.name;

    const imagem = await ImagemUtils.comprimirParaBase64(file);
    this.imagePreviewUrl = imagem.dataUrl;
    this.imageBase64 = imagem.base64;
  }

  onClearImage() {
    this.fileName = null;
    this.imageBase64 = null;
    this.imagePreviewUrl = null;
    // Reseta o estado interno do p-fileUpload (lista de arquivos selecionados)
    if (this.fileUploadRef) {
      this.fileUploadRef.clear();
    }
  }

  // =========================================================================
  // Imagem na tabela
  // =========================================================================

  marcaTemImagem(marca: any): boolean {
    return !!marca.imagem && String(marca.imagem).trim().length > 0;
  }

  getImagemUrl(imagem: string): string {
    if (!imagem) {
      return '';
    }
    if (imagem.startsWith('http') || imagem.startsWith('data:image')) {
      return imagem;
    }
    return `${this.env.imgHost}/${this.idEmpresa}/marca/${imagem}`;
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
    const placeholder = (event.target as HTMLElement).nextElementSibling;
    if (placeholder) {
      (placeholder as HTMLElement).style.display = 'inline';
    }
  }

  abrirDialogImagem(imagem: string): void {
    this.imagemExpandidaUrl = this.getImagemUrl(imagem);
    this.displayDialogImagem = true;
  }

  onImagemExpandidaError(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  // =========================================================================
  // Tabela
  // =========================================================================

  findMarca(page: number = 0, size: number = this.rows, sort: string = 'id,asc') {
    this.loading = true;
    this.marcaService.get('/marca', { page, size, sort }).subscribe({
      next: (response) => {
        this.marcasOriginal = response.content || [];
        this.marcas = [...this.marcasOriginal];
        this.totalRecords = response.totalElements;
        this.first = page * size;
        this.loading = false;

        if (this.searchNome && this.searchNome.trim().length > 0) {
          this.filtrarLocal(this.searchNome.trim());
        }
      },
      error: () => {
        this.marcasOriginal = [];
        this.marcas = [];
        this.totalRecords = 0;
        this.loading = false;
      },
    });
  }

  onSearchChange(value: string): void {
    this.searchNome = value || '';
    if (this.searchNome.trim().length > 0) {
      this.first = 0;
      this.filtrarLocal(this.searchNome.trim());
    } else {
      this.marcas = [...this.marcasOriginal];
      this.totalRecords = this.marcasOriginal.length;
    }
  }

  private filtrarLocal(term: string): void {
    const termLower = term.toLowerCase();

    this.marcas = this.marcasOriginal.filter(marca => {
      if (this.isColumnVisible('codigoMarca') && marca.codigoMarca != null) {
        if (String(marca.codigoMarca).includes(termLower)) return true;
      }
      if (this.isColumnVisible('nome') && marca.nome) {
        if (marca.nome.toLowerCase().includes(termLower)) return true;
      }
      if (this.isColumnVisible('criadoEm') && (marca as any).criadoEm) {
        const dataFormatada = this.formatarDataEHora((marca as any).criadoEm);
        if (dataFormatada.toLowerCase().includes(termLower)) return true;
      }
      return false;
    });

    this.totalRecords = this.marcas.length;
  }

  onPageChange(event: any) {
    const page = event.page;
    const size = event.rows;
    this.findMarca(page, size, `${this.lastfield},${this.lastSort}`);
  }

  onRowsPerPageChange() {
    if (this.rows === this._lastRows) return;
    this._lastRows = this.rows;
    this.first = 0;
    this.findMarca(0, this.rows, `${this.lastfield},${this.lastSort}`);
  }

  customSort(event: SortEvent): void {
    if (!event.field || !event.data) return;

    const field = event.field;
    const order = event.order ?? 1;

    this.lastfield = field;
    this.lastSort = order === 1 ? 'asc' : 'desc';

    event.data.sort((a: any, b: any) => {
      const valueA = this.getSortValue(a, field);
      const valueB = this.getSortValue(b, field);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return -1 * order;
      if (valueB == null) return 1 * order;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.localeCompare(valueB) * order;
      }

      return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * order;
    });
  }

  private getSortValue(item: any, field: string): any {
    const parts = field.split('.');
    let value: any = item;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    if (value != null && typeof value === 'object' && 'nome' in value) {
      return value.nome;
    }
    return value;
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  // =========================================================================
  // Formulario
  // =========================================================================

  submit() {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    if (!this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.texts.erro,
        detail: this.texts.nome_obrigatorio,
      });
      return;
    }

    const body: MarcaDTO = {
      nome: this.form.nome!,
      ...(this.imageBase64 ? { imagem: this.imageBase64 } : {}),
    };

    this.submitting = true;
    this.marcaService.post('/marca', body).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.texts.sucesso,
          detail: this.texts.marca_adicionada_sucesso,
        });
        this.limparForm();
        this.findMarca();
        this.activeTabIndex = 1;
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  salvarEdicaoDeMarca() {
    if (this.submitting) return;
    this.isFormSubmitted = true;
    if (!this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.texts.erro,
        detail: this.texts.nome_obrigatorio,
      });
      return;
    }

    const body: MarcaDTO = {
      nome: this.form.nome!,
      codigoMarca: this.form.codigoMarca!,
      ...(this.imageBase64 ? { imagem: this.imageBase64 } : {}),
    };

    this.submitting = true;
    this.marcaService.put('/marca/' + this.form.id, body).subscribe({
      next: () => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.texts.sucesso,
          detail: this.texts.marca_editada_sucesso,
        });
        this.limparForm();
        this.findMarca();
        this.activeTabIndex = 1;
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  deletarMarca(id: string) {
    if (this.embutido) return; // DELETE /marca fora do allowlist do contexto.
    this.deleteMarcaId = id;
    this.deleteDialogVisible = true;
  }

  onDeleteDialogShow() {
    setTimeout(() => {
      this.deleteConfirmBtnRef?.nativeElement?.focus();
    }, 50);
  }

  confirmarExclusao() {
    if (!this.deleteMarcaId) return;

    this.marcaService.delete(`/marca/${this.deleteMarcaId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.sucesso,
          detail: this.texts.marca_excluido_sucesso,
        });
        this.deleteDialogVisible = false;
        this.deleteMarcaId = null;
        this.findMarca();
      },
      error: () => {
        this.deleteDialogVisible = false;
        this.deleteMarcaId = null;
      },
    });
  }

  createEmptyForm(): MarcaFormDTO {
    return { nome: '', codigoMarca: null };
  }

  limparForm() {
    this.form = this.createEmptyForm();
    this.onClearImage();
    this.isFormSubmitted = false;

    // Voltar titulo para modo criacao
    this.texts.titulo = this.translate.instant('marca.texts.titulo_criar');
    this.texts.subtitulo = this.translate.instant('marca.texts.subtitulo_criar');
  }

  editarMarca(event: MarcaFormDTO) {
    this.form = { ...event };

    // Atualizar titulo para modo edicao
    this.texts.titulo = this.translate.instant('marca.texts.titulo_editar');
    this.texts.subtitulo = this.translate.instant('marca.texts.subtitulo_editar');

    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  novaMarca(): void {
    this.limparForm();
    this.activeTabIndex = 0;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // =========================================================================
  // Colunas
  // =========================================================================

  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  // =========================================================================
  // Atalhos de teclado
  // =========================================================================

  @HostListener('document:keydown', ['$event'])
  onGlobalKeydown(ev: KeyboardEvent): void {
    if (this.deleteDialogVisible) {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.confirmarExclusao();
        return;
      }
      if (ev.key === 'Escape') {
        ev.preventDefault();
        this.deleteDialogVisible = false;
        return;
      }
      return;
    }

    if (ev.key === 'F2') {
      ev.preventDefault();
      setTimeout(() => {
        this.searchInputRef?.nativeElement?.focus();
      }, 100);
      return;
    }

    if (ev.ctrlKey && ev.key === 'Enter') {
      ev.preventDefault();
      this.form.id ? this.salvarEdicaoDeMarca() : this.submit();
      return;
    }
  }
}
